type DraftField = {
  id: number;
  name?: string;
  nativeField?: string | null;
  type?: string | null;
};

type DraftSection = {
  fields?: DraftField[];
};

type DraftStep = {
  fields?: DraftField[];
  sections?: DraftSection[];
};

type DraftCurrentResponse = {
  draftId?: number;
  schema?: DraftStep[];
};

type FetchDataFn = (url: string) => Promise<any>;
type PostDataFn = (args: {
  url: string;
  data: any;
  skipCacheRefresh?: boolean;
}) => Promise<any>;

const normalize = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

const NATIVE_FIELD_ALIASES: Record<string, string[]> = {
  name: [
    "name",
    "nom",
    "projectname",
    "nomduprojet",
    "titre",
  ],
  type: [
    "type",
    "projecttype",
    "typeprojet",
    "typeduprojet",
  ],
};

const getFieldIdByNativeKey = (schema: DraftStep[], nativeKey: string): number | null => {
  const targets = new Set(
    (NATIVE_FIELD_ALIASES[nativeKey] ?? [nativeKey]).map((key) =>
      normalize(key),
    ),
  );
  for (const step of schema) {
    const rootFields = Array.isArray(step?.fields) ? step.fields : [];
    const sectionFields = Array.isArray(step?.sections)
      ? step.sections.flatMap((section) => (Array.isArray(section?.fields) ? section.fields : []))
      : [];
    for (const field of [...rootFields, ...sectionFields]) {
      const keys = [
        field?.nativeField,
        field?.name,
        field?.type,
      ].map((value) => normalize(String(value ?? "")));
      if (keys.some((key) => targets.has(key)) && Number.isFinite(Number(field?.id))) {
        return Number(field.id);
      }
    }
  }
  return null;
};

export const createProjectFromTypeSelection = async ({
  fetchData,
  postData,
  programId,
  defaultProjectName,
  typeId,
  draftScope,
  allowCreateWithoutNativeFields = false,
}: {
  fetchData: FetchDataFn;
  postData: PostDataFn;
  programId: number;
  defaultProjectName: string;
  typeId: number;
  allowCreateWithoutNativeFields?: boolean;
  draftScope?: {
    projectTypeId?: number;
    callId?: number;
    preservedCallId?: number;
  };
}): Promise<{ success: boolean; projectId?: number; message?: string }> => {
  if (!programId || !typeId) {
    return { success: false, message: "Programme ou type de projet invalide." };
  }

  const query = new URLSearchParams();
  if (programId) {
    query.set("programId", String(programId));
  }
  if (draftScope?.projectTypeId) {
    query.set("projectTypeId", String(draftScope.projectTypeId));
  }
  if (draftScope?.callId) {
    query.set("callId", String(draftScope.callId));
  }
  if (draftScope?.preservedCallId) {
    query.set("preservedCallId", String(draftScope.preservedCallId));
  }
  const currentDraftUrl = `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/current${
    query.toString() ? `?${query.toString()}` : ""
  }`;

  const currentDraft = (await fetchData(
    currentDraftUrl,
  )) as DraftCurrentResponse | null;

  if (!currentDraft?.draftId) {
    return { success: false, message: "Brouillon introuvable." };
  }

  const resetResult = await postData({
    url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/reset`,
    data: { draftId: currentDraft.draftId },
    skipCacheRefresh: true,
  });
  if (!resetResult?.success) {
    return { success: false, message: "Impossible de reinitialiser le brouillon." };
  }

  const refreshedDraft = (await fetchData(
    currentDraftUrl,
  )) as DraftCurrentResponse | null;
  const draftId = Number(refreshedDraft?.draftId ?? currentDraft.draftId);
  const schema = Array.isArray(refreshedDraft?.schema) ? refreshedDraft!.schema! : [];

  const nameFieldId = getFieldIdByNativeKey(schema, "name");
  const typeFieldId = getFieldIdByNativeKey(schema, "type");
  if (!nameFieldId && !typeFieldId) {
    if (!allowCreateWithoutNativeFields) {
      return { success: false, message: "Champs natifs Nom/Type introuvables dans le formulaire." };
    }

    const createResult = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/submit`,
      data: {
        draftId,
        programId,
        projectTypeId: typeId,
        projectName: defaultProjectName,
        mode: "create_only",
      },
      skipCacheRefresh: true,
    });

    if (!createResult?.success || !createResult?.projectId) {
      return { success: false, message: "La creation du projet a echoue." };
    }

    return { success: true, projectId: Number(createResult.projectId) };
  }

  const answers = [
    nameFieldId ? { fieldId: nameFieldId, value: defaultProjectName } : null,
    typeFieldId ? { fieldId: typeFieldId, value: String(typeId) } : null,
  ].filter(Boolean);

  if (answers.length > 0) {
    const saveResult = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/save-step`,
      data: {
        draftId,
        programId,
        projectTypeId: typeId,
        answers,
      },
      skipCacheRefresh: true,
    });
    if (!saveResult?.success) {
      return { success: false, message: "Impossible d'enregistrer le type de projet." };
    }
  }

  const createResult = await postData({
    url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/submit`,
    data: {
      draftId,
      programId,
      projectTypeId: typeId,
      projectName: defaultProjectName,
      mode: "create_only",
    },
    skipCacheRefresh: true,
  });

  if (!createResult?.success || !createResult?.projectId) {
    return { success: false, message: "La creation du projet a echoue." };
  }

  return { success: true, projectId: Number(createResult.projectId) };
};
